import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  generateQRCodeAPI,
  fetchLevelsAPI,
  fetchStudentsAPI,
  fetchAssignmentsAPI,
  uploadAssignmentAPI,
  deleteAssignmentAPI,
  downloadAssignmentAPI,
  downloadQRPdfAPI,
  createClassNoticeAPI,
  fetchPaperAssignmentRecordsAPI,
  deletePaperAssignmentRecordAPI,
  downloadStudentSubmissionAPI,
  gradeStudentSubmissionAPI,
  fetchTeacherStudentsOnlineAssignmentsAPI
} from "./paperAssignmentAPI";

// Async thunks
export const generateQRCode = createAsyncThunk(
  "paperAssignment/generateQRCode",
  async (data, { rejectWithValue }) => {
    try {
      const response = await generateQRCodeAPI(data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchLevels = createAsyncThunk(
  "paperAssignment/fetchLevels",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchLevelsAPI();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchStudents = createAsyncThunk(
  "paperAssignment/fetchStudents",
  async (levelId, { rejectWithValue }) => {
    try {
      const response = await fetchStudentsAPI(levelId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchAssignments = createAsyncThunk(
  "paperAssignment/fetchAssignments",
  async (levelId, { rejectWithValue }) => {
    try {
      const response = await fetchAssignmentsAPI(levelId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const uploadAssignment = createAsyncThunk(
  "paperAssignment/uploadAssignment",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await uploadAssignmentAPI(formData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteAssignment = createAsyncThunk(
  "paperAssignment/deleteAssignment",
  async (s3Key, { rejectWithValue }) => {
    try {
      const response = await deleteAssignmentAPI(s3Key);
      return { s3Key, ...response };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const downloadAssignment = createAsyncThunk(
  "paperAssignment/downloadAssignment",
  async (s3Key, { rejectWithValue }) => {
    try {
      const response = await downloadAssignmentAPI(s3Key);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const downloadQRPdf = createAsyncThunk(
  "paperAssignment/downloadQRPdf",
  async (data, { rejectWithValue }) => {
    try {
      const response = await downloadQRPdfAPI(data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createClassNotice = createAsyncThunk(
  "paperAssignment/createClassNotice",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await createClassNoticeAPI(formData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchPaperAssignmentRecords = createAsyncThunk(
  "paperAssignment/fetchPaperAssignmentRecords",
  async ({ levelId = null, studentId = null } = {}, { rejectWithValue }) => {
    try {
      const response = await fetchPaperAssignmentRecordsAPI(levelId, studentId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deletePaperAssignmentRecord = createAsyncThunk(
  "paperAssignment/deletePaperAssignmentRecord",
  async (recordId, { rejectWithValue }) => {
    try {
      const response = await deletePaperAssignmentRecordAPI(recordId);
      return { recordId, ...response };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const downloadStudentSubmission = createAsyncThunk(
  "paperAssignment/downloadStudentSubmission",
  async (s3Key, { rejectWithValue }) => {
    try {
      const response = await downloadStudentSubmissionAPI(s3Key);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const gradeStudentSubmission = createAsyncThunk(
  "paperAssignment/gradeStudentSubmission",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await gradeStudentSubmissionAPI(formData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchTeacherStudentsOnlineAssignments = createAsyncThunk(
  "paperAssignment/fetchTeacherStudentsOnlineAssignments",
  async ({ levelId = null, studentId = null, isFinished = null } = {}, { rejectWithValue }) => {
    try {
      const response = await fetchTeacherStudentsOnlineAssignmentsAPI(levelId, studentId, isFinished);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Initial state
const initialState = {
  levels: [],
  students: [],
  assignments: [],
  paperAssignmentRecords: [],
  onlineAssignments: [],
  qrCodeData: null,
  loading: false,
  error: null,
};

// Slice
const paperAssignmentSlice = createSlice({
  name: "paperAssignment",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearQRCode: (state) => {
      state.qrCodeData = null;
    },
    clearStudents: (state) => {
      state.students = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Generate QR Code
      .addCase(generateQRCode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generateQRCode.fulfilled, (state, action) => {
        state.loading = false;
        state.qrCodeData = action.payload;
      })
      .addCase(generateQRCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch Levels
      .addCase(fetchLevels.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLevels.fulfilled, (state, action) => {
        state.loading = false;
        state.levels = action.payload;
      })
      .addCase(fetchLevels.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch Students
      .addCase(fetchStudents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStudents.fulfilled, (state, action) => {
        state.loading = false;
        state.students = action.payload;
      })
      .addCase(fetchStudents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch Assignments
      .addCase(fetchAssignments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAssignments.fulfilled, (state, action) => {
        state.loading = false;
        state.assignments = action.payload;
      })
      .addCase(fetchAssignments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Upload Assignment
      .addCase(uploadAssignment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadAssignment.fulfilled, (state, action) => {
        state.loading = false;
        // 업로드 후 assignments 목록 새로고침 필요
      })
      .addCase(uploadAssignment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Delete Assignment
      .addCase(deleteAssignment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAssignment.fulfilled, (state, action) => {
        state.loading = false;
        // 삭제된 파일을 assignments에서 제거
        state.assignments = state.assignments.filter(
          assignment => assignment.key !== action.payload.s3Key
        );
      })
      .addCase(deleteAssignment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Download Assignment
      .addCase(downloadAssignment.pending, (state) => {
        state.error = null;
      })
      .addCase(downloadAssignment.fulfilled, (state, action) => {
        // 다운로드 성공 시 별도 처리 없음 (파일 다운로드는 컴포넌트에서 처리)
      })
      .addCase(downloadAssignment.rejected, (state, action) => {
        state.error = action.payload;
      })
      
      // Download QR PDF
      .addCase(downloadQRPdf.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(downloadQRPdf.fulfilled, (state, action) => {
        state.loading = false;
        // PDF 다운로드 성공 시 별도 처리 없음 (파일 다운로드는 컴포넌트에서 처리)
      })
      .addCase(downloadQRPdf.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Create Class Notice
      .addCase(createClassNotice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createClassNotice.fulfilled, (state, action) => {
        state.loading = false;
        // 성공 시 별도 처리 없음 (모달에서 처리)
      })
      .addCase(createClassNotice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch Paper Assignment Records
      .addCase(fetchPaperAssignmentRecords.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPaperAssignmentRecords.fulfilled, (state, action) => {
        state.loading = false;
        state.paperAssignmentRecords = action.payload;
      })
      .addCase(fetchPaperAssignmentRecords.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Delete Paper Assignment Record
      .addCase(deletePaperAssignmentRecord.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePaperAssignmentRecord.fulfilled, (state, action) => {
        state.loading = false;
        // Remove deleted record from state
        state.paperAssignmentRecords = state.paperAssignmentRecords.filter(
          record => record.id !== action.payload.recordId
        );
      })
      .addCase(deletePaperAssignmentRecord.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Teacher Students Online Assignments
      .addCase(fetchTeacherStudentsOnlineAssignments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTeacherStudentsOnlineAssignments.fulfilled, (state, action) => {
        state.loading = false;
        state.onlineAssignments = action.payload;
      })
      .addCase(fetchTeacherStudentsOnlineAssignments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearQRCode, clearStudents } = paperAssignmentSlice.actions;

export default paperAssignmentSlice.reducer;